import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle, Clock, XCircle, CalendarDays, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import QrScanner from '@/components/QrScanner';
import api from '@/lib/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminAnalyticsTab({ analytics, analyticsLoading, fetchAnalytics }) {
  const [analyticsPeriod, setAnalyticsPeriod] = useState('30d');
  const [analyticsCustomStart, setAnalyticsCustomStart] = useState('');
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState('');
  const [checkInCode, setCheckInCode] = useState('');
  const [checkInResult, setCheckInResult] = useState(null);
  const [checkInLoading, setCheckInLoading] = useState(false);

  const handlePeriodChange = (p) => {
    setAnalyticsPeriod(p);
    if (p !== 'custom') fetchAnalytics(p);
  };

  const handleCustomDateApply = () => {
    if (analyticsCustomStart && analyticsCustomEnd) {
      setAnalyticsPeriod('custom');
      fetchAnalytics('custom', analyticsCustomStart, analyticsCustomEnd);
    }
  };

  const handleCheckIn = async () => {
    if (!checkInCode.trim()) return;
    setCheckInLoading(true);
    setCheckInResult(null);
    try {
      const res = await api.post(`/events/admin/check-in/${checkInCode.trim()}`, {}, { headers: getAuthHeader() });
      setCheckInResult(res.data);
      if (res.data.status === 'checked_in') toast.success(`${res.data.user_name} checked in!`);
      else if (res.data.status === 'already_checked_in') toast.info(`${res.data.user_name} was already checked in`);
    } catch (error) {
      setCheckInResult({ status: 'error', message: error.response?.data?.detail || 'Invalid ticket' });
      toast.error(error.response?.data?.detail || 'Invalid ticket code');
    } finally {
      setCheckInLoading(false);
    }
  };

  if (analyticsLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={32} /></div>;
  }

  if (!analytics) {
    return <p className="text-center text-text-mid py-12">Click the Analytics tab to load data</p>;
  }

  return (
    <div className="space-y-6">
      {/* Date Range Picker */}
      <div className="bg-white rounded-2xl shadow-card p-4 flex flex-wrap items-center gap-3" data-testid="analytics-date-range">
        <CalendarDays size={18} className="text-navy" />
        <span className="text-sm font-medium text-navy mr-1">Period:</span>
        {[
          { value: '7d', label: '7 Days' },
          { value: '30d', label: '30 Days' },
          { value: '90d', label: '90 Days' },
          { value: 'all', label: 'All Time' },
        ].map((opt) => (
          <Button
            key={opt.value}
            size="sm"
            variant={analyticsPeriod === opt.value ? 'default' : 'outline'}
            className={`rounded-full text-xs px-4 ${analyticsPeriod === opt.value ? 'bg-teal hover:bg-teal-deep text-white' : 'border-border-subtle text-text-mid hover:bg-cream/50'}`}
            onClick={() => handlePeriodChange(opt.value)}
            data-testid={`period-${opt.value}`}
          >
            {opt.label}
          </Button>
        ))}
        <div className="h-5 w-px bg-border-subtle mx-1" />
        <div className="flex items-center gap-2">
          <Input type="date" value={analyticsCustomStart} onChange={(e) => setAnalyticsCustomStart(e.target.value)} className="rounded-lg text-xs h-8 w-[130px]" data-testid="custom-start-date" />
          <span className="text-xs text-text-mid">to</span>
          <Input type="date" value={analyticsCustomEnd} onChange={(e) => setAnalyticsCustomEnd(e.target.value)} className="rounded-lg text-xs h-8 w-[130px]" data-testid="custom-end-date" />
          <Button size="sm" variant="outline" onClick={handleCustomDateApply} disabled={!analyticsCustomStart || !analyticsCustomEnd} className="rounded-full text-xs px-3 border-navy text-navy hover:bg-navy hover:text-white" data-testid="apply-custom-range">Apply</Button>
        </div>
        {analytics?.period && (
          <span className="text-xs text-text-light ml-auto">
            {analytics.period.label === 'custom' ? `${analytics.period.start?.slice(0,10)} — ${analytics.period.end?.slice(0,10)}` : analytics.period.label === 'all' ? 'Showing all time data' : `Last ${analytics.period.label}`}
          </span>
        )}
        <div className="flex gap-2 ml-2">
          <Button size="sm" variant="outline" className="rounded-full text-xs px-3 border-border-subtle text-text-mid hover:bg-cream/50" data-testid="export-csv-btn" onClick={() => { let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/export?format=csv`; if (analyticsPeriod === 'custom' && analyticsCustomStart && analyticsCustomEnd) url += `&start_date=${analyticsCustomStart}&end_date=${analyticsCustomEnd}`; else if (analyticsPeriod !== 'all') url += `&period=${analyticsPeriod}`; const token = localStorage.getItem('token') || localStorage.getItem('adminToken'); fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'calimoms_analytics.csv'; a.click(); }); }}>CSV</Button>
          <Button size="sm" variant="outline" className="rounded-full text-xs px-3 border-border-subtle text-text-mid hover:bg-cream/50" data-testid="export-pdf-btn" onClick={() => { let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/export?format=pdf`; if (analyticsPeriod === 'custom' && analyticsCustomStart && analyticsCustomEnd) url += `&start_date=${analyticsCustomStart}&end_date=${analyticsCustomEnd}`; else if (analyticsPeriod !== 'all') url += `&period=${analyticsPeriod}`; const token = localStorage.getItem('token') || localStorage.getItem('adminToken'); fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'calimoms_analytics.pdf'; a.click(); }); }}>PDF</Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-card p-5 text-center" data-testid="kpi-mrr">
          <p className="text-sm text-text-mid mb-1">Est. MRR</p>
          <p className="text-3xl font-bold text-teal">${analytics.revenue.mrr.toLocaleString()}</p>
          <p className="text-xs text-text-mid mt-1">{analytics.membership.connect} Connect + {analytics.membership.thrive} Thrive</p>
        </div>
        <div className="bg-white rounded-2xl shadow-card p-5 text-center" data-testid="kpi-event-revenue">
          <p className="text-sm text-text-mid mb-1">Event Revenue</p>
          <p className="text-3xl font-bold text-navy">${analytics.revenue.event_revenue_total.toLocaleString()}</p>
          <p className="text-xs text-text-mid mt-1">{analytics.revenue.total_tickets_sold} tickets sold</p>
        </div>
        <div className="bg-white rounded-2xl shadow-card p-5 text-center" data-testid="kpi-total-users">
          <p className="text-sm text-text-mid mb-1">Total Members</p>
          <p className="text-3xl font-bold text-navy">{analytics.membership.total_users}</p>
          <p className="text-xs text-text-mid mt-1">+{analytics.membership.new_users_period} in period</p>
        </div>
        <div className="bg-white rounded-2xl shadow-card p-5 text-center" data-testid="kpi-churn">
          <p className="text-sm text-text-mid mb-1">Churn Rate</p>
          <p className="text-3xl font-bold" style={{color: analytics.membership.churn_rate > 5 ? '#dc2626' : '#16a34a'}}>{analytics.membership.churn_rate}%</p>
          <p className="text-xs text-text-mid mt-1">{analytics.membership.cancelled_period} cancellations</p>
        </div>
      </div>

      {/* Membership & Signup Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-card p-6">
          <h3 className="font-semibold text-navy mb-4">Membership Breakdown</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={[{ name: 'Connect (Free)', value: analytics.membership.connect }, { name: 'Thrive (Paid)', value: analytics.membership.thrive }]} cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2} dataKey="value" label={({name, value}) => value > 0 ? `${name}: ${value}` : ''}>
                <Cell fill="#94a3b8" /><Cell fill="#4a9b8f" /><Cell fill="#0d3b66" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          {analytics.membership.trialing > 0 && <p className="text-center text-sm text-text-mid mt-2">{analytics.membership.trialing} currently trialing</p>}
        </div>
        <div className="bg-white rounded-2xl shadow-card p-6">
          <h3 className="font-semibold text-navy mb-4">Weekly Signups (90 days)</h3>
          {analytics.signup_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={analytics.signup_trend}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="week" tick={{fontSize: 11}} tickFormatter={(v) => v.slice(5)} /><YAxis tick={{fontSize: 11}} allowDecimals={false} /><Tooltip /><Bar dataKey="signups" fill="#4a9b8f" radius={[4,4,0,0]} /></BarChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-text-mid py-16">No signup data yet</p>}
        </div>
      </div>

      {/* Event & Revenue Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-card p-6">
          <h3 className="font-semibold text-navy mb-4">Event Overview</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-cream/50 rounded-xl p-4 text-center"><p className="text-2xl font-bold text-navy">{analytics.events.total}</p><p className="text-xs text-text-mid">Total Events</p></div>
            <div className="bg-cream/50 rounded-xl p-4 text-center"><p className="text-2xl font-bold text-teal">{analytics.events.total_rsvps}</p><p className="text-xs text-text-mid">Total RSVPs</p></div>
            <div className="bg-cream/50 rounded-xl p-4 text-center"><p className="text-2xl font-bold text-navy">{analytics.events.paid}</p><p className="text-xs text-text-mid">Paid Events</p></div>
            <div className="bg-cream/50 rounded-xl p-4 text-center"><p className="text-2xl font-bold text-text-mid">{analytics.events.free}</p><p className="text-xs text-text-mid">Free Events</p></div>
          </div>
          {analytics.events.top_earning.length > 0 && (
            <>
              <h4 className="text-sm font-semibold text-navy mt-4 mb-2">Top Earning Events</h4>
              <div className="space-y-2">
                {analytics.events.top_earning.map((e, i) => (
                  <div key={i} className="flex justify-between items-center text-sm bg-cream/30 rounded-lg px-3 py-2">
                    <span className="text-navy font-medium truncate mr-2">{e.title}</span>
                    <span className="text-green-700 font-semibold whitespace-nowrap">${e.revenue.toFixed(2)} ({e.tickets} tix)</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="bg-white rounded-2xl shadow-card p-6">
          <h3 className="font-semibold text-navy mb-4">Event Revenue Trend</h3>
          {analytics.revenue.event_revenue_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={analytics.revenue.event_revenue_trend}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="month" tick={{fontSize: 11}} /><YAxis tick={{fontSize: 11}} tickFormatter={(v) => `$${v}`} /><Tooltip formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} /><Line type="monotone" dataKey="revenue" stroke="#0d3b66" strokeWidth={2} dot={{ fill: '#0d3b66' }} /></LineChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-text-mid py-16">No event revenue data yet</p>}
        </div>
      </div>

      {/* Engagement */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <h3 className="font-semibold text-navy mb-4">Community Engagement</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center p-3 bg-cream/50 rounded-xl"><p className="text-xl font-bold text-teal">{analytics.engagement.total_matches}</p><p className="text-xs text-text-mid">Matches</p></div>
          <div className="text-center p-3 bg-cream/50 rounded-xl"><p className="text-xl font-bold text-navy">{analytics.engagement.total_messages}</p><p className="text-xs text-text-mid">Messages</p></div>
          <div className="text-center p-3 bg-cream/50 rounded-xl"><p className="text-xl font-bold text-teal">{analytics.engagement.messages_period}</p><p className="text-xs text-text-mid">Msgs (period)</p></div>
          <div className="text-center p-3 bg-cream/50 rounded-xl"><p className="text-xl font-bold text-navy">{analytics.engagement.total_posts}</p><p className="text-xs text-text-mid">Posts</p></div>
          <div className="text-center p-3 bg-cream/50 rounded-xl"><p className="text-xl font-bold text-teal">{analytics.engagement.posts_period}</p><p className="text-xs text-text-mid">Posts (period)</p></div>
          <div className="text-center p-3 bg-cream/50 rounded-xl"><p className="text-xl font-bold text-navy">{analytics.engagement.total_listings}</p><p className="text-xs text-text-mid">Listings</p></div>
        </div>
      </div>

      {/* Check-In Section */}
      <div className="bg-white rounded-2xl shadow-card p-6" data-testid="check-in-section">
        <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
          <CheckCircle size={18} className="text-teal" /> Event Check-In
        </h3>
        <p className="text-sm text-text-mid mb-4">Scan a ticket QR code with your camera or enter the code manually.</p>
        <div className="mb-4">
          <QrScanner onScan={(code) => { setCheckInCode(code); setCheckInResult(null); (async () => { setCheckInLoading(true); try { const res = await api.post(`/events/admin/check-in/${code.trim()}`, {}, { headers: getAuthHeader() }); setCheckInResult(res.data); if (res.data.status === 'checked_in') toast.success(`${res.data.user_name} checked in!`); else if (res.data.status === 'already_checked_in') toast.info(`${res.data.user_name} was already checked in`); } catch (error) { setCheckInResult({ status: 'error', message: error.response?.data?.detail || 'Invalid ticket' }); toast.error(error.response?.data?.detail || 'Invalid ticket code'); } finally { setCheckInLoading(false); } })(); }} />
        </div>
        <div className="flex gap-3 max-w-md">
          <Input placeholder="Or type code (e.g. CMC-A1B2C3D4)" value={checkInCode} onChange={(e) => { setCheckInCode(e.target.value.toUpperCase()); setCheckInResult(null); }} className="rounded-xl" data-testid="check-in-input" onKeyDown={(e) => e.key === 'Enter' && handleCheckIn()} />
          <Button onClick={handleCheckIn} disabled={checkInLoading || !checkInCode.trim()} className="bg-teal hover:bg-teal-deep rounded-full px-6" data-testid="check-in-btn">{checkInLoading ? <Loader2 size={16} className="animate-spin" /> : 'Check In'}</Button>
        </div>
        {checkInResult && (
          <div className={`mt-4 p-4 rounded-xl ${checkInResult.status === 'checked_in' ? 'bg-green-50 border border-green-200' : checkInResult.status === 'already_checked_in' ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`} data-testid="check-in-result">
            {checkInResult.status === 'checked_in' && <p className="text-green-700 font-semibold flex items-center gap-2"><CheckCircle size={18} /> {checkInResult.user_name} — Checked In for {checkInResult.event_title}</p>}
            {checkInResult.status === 'already_checked_in' && <p className="text-yellow-700 font-semibold flex items-center gap-2"><Clock size={18} /> {checkInResult.user_name} — Already checked in</p>}
            {checkInResult.status === 'error' && <p className="text-red-700 font-semibold flex items-center gap-2"><XCircle size={18} /> {checkInResult.message}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
